import { useState, useRef, useEffect } from "react";
import { TextField } from "../common/inputFiled";
import toast from "react-hot-toast";

export default function ForgotPassword () {
  const [currentStep, setCurrentStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [formData, setFormData] = useState({
    email: "",
    otp: new Array(6).fill(""),
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const otpInputs = useRef([]);

  // ============ TIMER ============
  useEffect(() => {
    if (currentStep === 2 && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer, currentStep]);

  // ============ HANDLE INPUTS ============
  const handleChange = (e) => {
    const { name, value } = e.target;

    // compute updated form state so we can validate immediately
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    // clear field-specific error for the edited field
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    // Live validation for newPassword / confirmPassword
    if (name === "newPassword" || name === "confirmPassword") {
      const pass = updated.newPassword || "";
      const confirm = updated.confirmPassword || "";

      if (confirm.length > 0) {
        if (pass !== confirm) {
          setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
        } else {
          setErrors((prev) => {
            const copy = { ...prev };
            delete copy.confirmPassword;
            return copy;
          });
        }
      } else {
        // remove confirmPassword error when confirm is empty
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy.confirmPassword;
          return copy;
        });
      }
    }
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return;
    const newOtp = [...formData.otp];
    newOtp[index] = element.value;
    setFormData((prev) => ({ ...prev, otp: newOtp }));
    if (element.value && element.nextSibling) element.nextSibling.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && e.target.value === "" && index > 0)
      otpInputs.current[index - 1].focus();
  };

  // ============ STEP 1: EMAIL SUBMIT ============
  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email";
    if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

    try {
      setLoading(true);
      const payload = new URLSearchParams({ email: formData.email });
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const data = await res.json();
      if (data?.status === "1") {
        setCurrentStep(2);
        setResendTimer(30);
        setCanResend(false);
      } else throw new Error(data?.message || "Failed to send OTP");
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
      setErrors({ api: err.message });
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 2: OTP SUBMIT ============
  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    const enteredOtp = formData.otp.join("");
    if (enteredOtp.length !== 6 || formData.otp.some((d) => d === "")) {
      setErrors({ otp: "Please enter the complete code" });
      return;
    }

    try {
      setLoading(true);
      const payload = new URLSearchParams({
        email: formData.email,
        otp: enteredOtp,
      });

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/password-reset/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const data = await res.json();
      if (data?.status === "1") {
        setCurrentStep(3);
      } else {
        throw new Error(data?.message || "Invalid OTP. Try again.");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
      setErrors({ otp: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    try {
      setCanResend(false);
      setResendTimer(30);
      const payload = new URLSearchParams({ email: formData.email });
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
    } catch (err) {
      setErrors({ api: err.message });
    }
  };

  // ============ STEP 3: CHANGE PASSWORD ============
  const handleChangePassword = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.newPassword) newErrors.newPassword = "Enter new password";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Re-enter password";
    else if (formData.newPassword !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

    try {
      setLoading(true);
      const payload = new URLSearchParams({
        email: formData.email,
        new_password: formData.newPassword,
      });

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/password-reset/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const data = await res.json();
      if (data?.status === "1") {
        toast.success("Password updated successfully!");
        window.location.href = "/login";
      } else {
        throw new Error(data?.message || "Failed to reset password");
      }
    } catch (err) {
      toast.error(err.message);
      setErrors({ api: err.message });
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 1 UI ============
  if (currentStep === 1) {
    return (
      <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-6 sm:py-8">
        <div className="w-full max-w-[520px] p-6 sm:p-8 md:p-10">
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
              <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
            </div>

            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
               PhoneBookPlus
            </h1>
          </div>

          <div className="space-y-3 mb-3 text-center">
            <h2 className="text-xl text-[#000000] font-medium">
              Forgot Password
            </h2>
            <p className="text-[#475467] font-medium">
              Reset password code will be sent to your email address.
            </p>
          </div>
          <form className="space-y-5" onSubmit={handleSubmitEmail}>
            <TextField
              id="email"
              type="email"
              name="email"
              label="Email"
              startIcon="./input-logos/password.svg"
              placeholder="Enter Email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
            />


            <button
              type="submit"
              className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3 text-white font-semibold"
            >
              {loading ? "Sending..." : "Send Code"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ============ STEP 2 UI ============
  if (currentStep === 2) {
    return (
      <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-6 sm:py-8">
        <div className="w-full max-w-[520px] p-6 sm:p-8 md:p-10">
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
              <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
            </div>

            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
               PhoneBookPlus
            </h1>
          </div>

          <div className="text-center mb-4">
            <h2 className="text-xl text-[#000000] font-medium mb-1">
              Verify Your Email
            </h2>
            <p className="text-[#475467] font-medium">
              A reset code has been sent to{" "}
              <span className="font-semibold">{formData.email}</span>.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmitOtp}>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Enter Verification Code
              </label>
              <div className="flex justify-between space-x-2">
                {formData.otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={data}
                    ref={(ref) => (otpInputs.current[index] = ref)}
                    onChange={(e) => handleOtpChange(e.target, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    onFocus={(e) => e.target.select()}
                    className="w-12 h-12 rounded-xl border border-gray-400 bg-white text-center text-lg font-semibold text-gray-800 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-sm"
                  />
                ))}
              </div>

            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Haven’t received the code?{" "}
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-[#7C4DFF] hover:underline font-medium"
                  >
                    Resend it
                  </button>
                ) : (
                  <span className="text-gray-500">Resend in {resendTimer}s</span>
                )}
              </p>
            </div>

            {errors.api && (
              <p className="text-red-500 text-xs text-center">{errors.api}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ============ STEP 3 UI ============
  return (
    <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-[520px] p-6 sm:p-8 md:p-10">
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
            <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
             PhoneBookPlus
          </h1>
        </div>

        <div className="space-y-3 mb-3 text-center">
          <h2 className="text-xl text-[#000000] font-medium aoboshi-one">
            Change Password
          </h2>
        </div>
        <form className="space-y-5" onSubmit={handleChangePassword}>
          <TextField
            id="newPassword"
            type="password"
            name="newPassword"
            label="New Password"
            placeholder="Enter new password"
            value={formData.newPassword}
            onChange={handleChange}
            error={errors.newPassword}
          />
          <TextField
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            placeholder="Re-enter new password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
          />

          <button
            type="submit"
            className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3 text-white font-semibold"
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
